import React, { useEffect } from "react";

interface FormatChangeModalProps {
  open: boolean;
  newTotal?: number;
  currentTotal?: number;
  onCancel: () => void;
  onConfirm: () => void;
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const FormatChangeModal: React.FC<FormatChangeModalProps> = ({
  open,
  newTotal,
  currentTotal,
  onCancel,
  onConfirm,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const delta = (newTotal ?? 0) - (currentTotal ?? 0);
  const deltaLabel = `${delta >= 0 ? "+" : "-"}${formatCurrency(Math.abs(delta))}`;
  const hasCost = newTotal !== undefined;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onMouseDown={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="format-change-title"
        className="w-[420px] max-w-[90vw] rounded-xl bg-card p-6 shadow-[0px_10px_30px_rgba(2,6,23,0.18)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 id="format-change-title" className="text-base font-semibold text-foreground">
          Update to new design?
        </h2>
        <p className="text-muted-foreground mt-2 text-sm leading-5">
          The design you selected has a different format than your campaign.{" "}
          {hasCost ? (
            <>
              Updating will change your campaign total to{" "}
              <span className="text-foreground font-medium">{formatCurrency(newTotal ?? 0)}</span> ({deltaLabel}).
            </>
          ) : (
            "Updating will adjust your campaign total."
          )}
        </p>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            data-cy="formatChangeModalCancelButton"
            onClick={onCancel}
            className="text-foreground border-border h-9 rounded-lg border border-solid bg-background px-4 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            data-cy="formatChangeModalConfirmButton"
            onClick={onConfirm}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground"
          >
            Update to new design
          </button>
        </div>
      </div>
    </div>
  );
};
